/**
 * Shared formatting utilities for rate display
 * Used by both RatesGrid renderers and export builders
 */

import { CoverageTierEnum, RateUnitEnum } from "@platform/openapi-types";

import { AGE_BAND_AT_MOST, MAX_AGE_SENTINEL } from "../constants";

import { isVolumeBasedRate } from "./premium-calculations";
import { isLifeCoverageType } from "./rate-helpers";

import type { Rate } from "../types";

export function formatAgeBand(lower?: number, upper?: number): string {
  if (lower == null && upper == null) return "Unknown";
  if (lower == null) return `0-${upper}`;
  if (upper == null || upper >= 999) return `${lower}+`;
  if (lower === upper) return `${lower}`;
  return `${lower}-${upper}`;
}

export interface AgeBandLabelContext {
  /**
   * The band sits last in an ordered schedule, where one age repeated as both
   * bounds ("75-75") is how carriers express "75 and older".
   */
  readonly isHighestBand?: boolean;
  /** Critical Illness rates are quoted at milestone ages, not over ranges. */
  readonly isCriticalIllness?: boolean;
}

/**
 * Formats an age band for the rate grids and the rate structure questionnaire:
 * "≤19" for the lowest band, "20-24" in the middle, "75+" for the open-ended
 * highest band. Critical Illness quotes a milestone age rather than a range, so
 * its bands stay bare numbers ("65").
 */
export const formatAgeBandLabel = (
  lower?: number,
  upper?: number,
  {
    isHighestBand = false,
    isCriticalIllness = false,
  }: AgeBandLabelContext = {},
): string => {
  if (lower == null && upper == null) return "Unknown";
  if (lower == null) {
    return isCriticalIllness ? `${upper}` : `${AGE_BAND_AT_MOST}${upper}`;
  }

  // Open-ended is settled before the lowest-band rule so a band with neither
  // bound (0 to null / sentinel — one band covering every age) reads as "0+"
  // rather than "≤" with nothing to bound.
  const isOpenEnded = upper == null || upper >= MAX_AGE_SENTINEL;
  if (isOpenEnded) return isCriticalIllness ? `${lower}` : `${lower}+`;
  if (lower === 0 && !isCriticalIllness) return `${AGE_BAND_AT_MOST}${upper}`;
  if (lower === upper) {
    return isHighestBand && !isCriticalIllness ? `${lower}+` : `${lower}`;
  }
  return `${lower}-${upper}`;
};

// Temporary frontend-only fix: both 3-tier "Employee + 1 Dependent" and 5-tier
// "Employee & Child" currently come back from the backend as employeeDependent,
// which loses the original selection after save. We disambiguate by tier count when
// it is known, and otherwise fall back to a neutral label until the backend exposes
// a distinct value for these cases.
export function coverageTierToLabel(
  tierCode?: CoverageTierEnum | string,
  tierCount?: number,
): string {
  if (!tierCode) return "Unknown";

  switch (tierCode) {
    case CoverageTierEnum.employee:
      return "Employee";
    case CoverageTierEnum.employeeSpouse:
      return "EE+Spouse";
    case CoverageTierEnum.employeeChildren:
      return "EE+Children";
    case CoverageTierEnum.employeeFamily:
      return "Full Family";
    case CoverageTierEnum.employeeDependent:
      return tierCount === 5 ? "EE+Child" : "EE+1 Dep";
    case CoverageTierEnum.employee2Dependents:
      return tierCount === 5 ? "EE+2 Children" : "EE+2 Dep";
    case CoverageTierEnum.spouseOnly:
      return "Spouse";
    case CoverageTierEnum.spouseDependent:
      return "Deps Only";
    case CoverageTierEnum.spouseChildren:
      return "Spouse+Ch";
    case CoverageTierEnum.childOnly:
      return "Child";
    default:
      return String(tierCode);
  }
}

/**
 * Maps a coverage tier code to a long-form human-readable label
 * Used by CompositeRenderer for more descriptive tier names
 */
export function coverageTierToLabelLong(
  tierCode?: CoverageTierEnum | string,
  tierCount?: number,
): string {
  if (!tierCode) return "Unknown";

  switch (tierCode) {
    case CoverageTierEnum.employee:
      return "Employee Only";
    case CoverageTierEnum.employeeSpouse:
      return "Employee & Spouse";
    case CoverageTierEnum.employeeChildren:
      return "Employee & Children";
    case CoverageTierEnum.employeeFamily:
      return "Full Family";
    case CoverageTierEnum.employeeDependent:
      return tierCount === 5 ? "Employee & Child" : "Employee + 1 Dependent";
    case CoverageTierEnum.employee2Dependents:
      return "Employee & 2 Children";
    case CoverageTierEnum.spouseOnly:
      return "Spouse Only";
    case CoverageTierEnum.spouseDependent:
      return "Dependents Only";
    case CoverageTierEnum.spouseChildren:
      return "Spouse + Children";
    case CoverageTierEnum.childOnly:
      return "Child Only";
    default:
      return String(tierCode);
  }
}

/**
 * Canonical display order for coverage tiers, employee outward. Rates arrive in
 * whatever order the carrier's quote listed them, so tiered rate rows are sorted
 * for display rather than shown in payload order. Tiers with no code, or a code
 * not listed here, keep their relative position at the end.
 */
const COVERAGE_TIER_DISPLAY_ORDER: readonly CoverageTierEnum[] = [
  CoverageTierEnum.employee,
  CoverageTierEnum.employeeSpouse,
  CoverageTierEnum.employeeDependent,
  CoverageTierEnum.employee2Dependents,
  CoverageTierEnum.employeeChildren,
  CoverageTierEnum.employeeFamily,
  CoverageTierEnum.spouseOnly,
  CoverageTierEnum.spouseChildren,
  CoverageTierEnum.spouseDependent,
  CoverageTierEnum.childOnly,
];

const coverageTierDisplayIndex = (tierCode?: CoverageTierEnum): number => {
  if (!tierCode) return COVERAGE_TIER_DISPLAY_ORDER.length;

  const index = COVERAGE_TIER_DISPLAY_ORDER.indexOf(tierCode);
  return index === -1 ? COVERAGE_TIER_DISPLAY_ORDER.length : index;
};

/** Orders tiered rates by canonical coverage tier for display. */
export const sortRatesByCoverageTier = (rates: Rate[]): Rate[] =>
  [...rates].sort(
    (a, b) =>
      coverageTierDisplayIndex(a.coverageTierCode) -
      coverageTierDisplayIndex(b.coverageTierCode),
  );

// A rate with a lower bound (e.g. "50+" or "25-29") sorts by that bound. A
// rate with an upper bound but no lower bound (e.g. "<25") is open-ended
// below — the youngest band — and sorts before every bounded rate. Only a
// rate missing both bounds is genuinely unplaceable and sorts after
// everything. MIN_SAFE_INTEGER (not -Infinity) keeps every key finite so the
// comparator's subtraction never produces NaN when two open-below rates are
// compared.
const ageBandBoundsSortKey = (bounds: {
  lower?: number;
  upper?: number;
}): number => {
  if (bounds.lower != null) return bounds.lower;
  if (bounds.upper != null) return Number.MIN_SAFE_INTEGER;
  return Number.MAX_SAFE_INTEGER;
};

const ageBandSortKey = (rate: Rate): number =>
  ageBandBoundsSortKey({ lower: rate.ageBandLower, upper: rate.ageBandUpper });

/**
 * Orders age-banded rates youngest band first. Carriers quote bands in whatever
 * order their document lists them, and the grid groups by first appearance, so
 * without this a quote that starts at 30-34 renders its younger bands mid-table.
 */
export const sortRatesByAgeBand = (rates: Rate[]): Rate[] =>
  [...rates].sort((a, b) => ageBandSortKey(a) - ageBandSortKey(b));

/**
 * Sorts already-grouped age-band rows (lower/upper already resolved) the same
 * way sortRatesByAgeBand orders raw rates. A plain `(a.lower ?? 0) - (b.lower
 * ?? 0)` collides an open-ended band ("<25", lower == null) with a genuine
 * lower === 0 band and with every other open-ended band, so a stable sort
 * falls back to entry order instead of numeric order.
 */
export const sortByAgeBandBounds = <
  T extends { lower?: number; upper?: number },
>(
  groups: T[],
): T[] =>
  [...groups].sort((a, b) => ageBandBoundsSortKey(a) - ageBandBoundsSortKey(b));

export function getUnitLabel(unit?: string): string {
  switch (unit) {
    case "volumePer10":
      return "Rate per $10";
    case "volumePer100":
      return "Rate per $100";
    case "volumePer1000":
      return "Rate per $1,000";
    case "per10OfSalary":
      return "Rate per $10 of Salary";
    case "per100OfSalary":
      return "Rate per $100 of Salary";
    case "per1000OfSalary":
      return "Rate per $1,000 of Salary";
    case "usd":
    default:
      return "Rate";
  }
}

export function getQuantityLabel(
  unit?: string,
  isPepm?: boolean,
  coverageType?: string,
): string {
  if (isPepm) {
    return "Number of Enrolled";
  }
  switch (unit) {
    case "per10OfSalary":
    case "per100OfSalary":
    case "per1000OfSalary":
      return "Covered Payroll";
    case "usd":
      return "Number of Units";
    case "volumePer10":
    case "volumePer100":
    case "volumePer1000":
    default:
      return isLifeCoverageType(coverageType)
        ? "Insured Volume"
        : "Covered Benefit";
  }
}

export const getEnrolledLabel = (
  isVolumeBased: boolean,
  unit?: RateUnitEnum,
  coverageType?: string,
): string =>
  isVolumeBased ? getQuantityLabel(unit, false, coverageType) : "Enrolled";

/** Quantity column label for one coverage tier, from that tier's own rows. */
export const getTierEnrolledLabel = (
  rates: Rate[],
  tier: CoverageTierEnum,
  coverageType?: string,
): string => {
  const tierRate = rates.find((rate) => rate.coverageTierCode === tier);
  return getEnrolledLabel(
    isVolumeBasedRate(tierRate),
    tierRate?.unit,
    coverageType,
  );
};

/**
 * Check if rates have employee/spouse split for age banded designs.
 *
 * AI extraction tags every rate in some untiered age-banded schedules (LTD/STD,
 * unlabeled voluntary life) as the employee tier by default, so an employee tag
 * alone isn't evidence of a split — only a genuine spouse rate is.
 */
export function hasEmployeeSpouseSplit(rates: Rate[] | undefined): boolean {
  if (!rates) return false;
  return rates.some(
    (rate) => rate.coverageTierCode === CoverageTierEnum.spouseOnly,
  );
}

/**
 * Create a unique key for an age band
 */
export function createAgeBandKey(lower?: number, upper?: number): string {
  return `${lower ?? "null"}-${upper ?? "null"}`;
}

/**
 * Format cell value for display (handles undefined/null)
 */
export function formatCellValue(value: number | undefined): string {
  if (value == null) return "";
  return String(value);
}

const VOLUME_RATE_UNITS = new Set<string>([
  RateUnitEnum.volumePer10,
  RateUnitEnum.volumePer100,
  RateUnitEnum.volumePer1000,
  RateUnitEnum.per10OfSalary,
  RateUnitEnum.per100OfSalary,
  RateUnitEnum.per1000OfSalary,
]);

/**
 * Format rate value with the correct decimal precision for its unit:
 * volume-based units (per $10/$100/$1,000 of coverage or salary) use 3 decimal places,
 * all others (USD, PEPM) use 2. Basic and Voluntary Life rates use 3 in every unit,
 * including a flat per-unit (USD) rate.
 */
export function formatRateValue(
  rate: number | null | undefined,
  unit?: string,
  coverageType?: string,
): string {
  if (rate == null) return "";
  return VOLUME_RATE_UNITS.has(unit ?? "") || isLifeCoverageType(coverageType)
    ? rate.toFixed(3)
    : rate.toFixed(2);
}

/**
 * Insert thousands separators into the integer portion of an unsigned numeric
 * string, preserving any decimal portion (e.g. "1234.5" → "1,234.5").
 */
const groupThousandsDigits = (unsigned: string): string => {
  const [intPart, decimalPart] = unsigned.split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart != null ? `${groupedInt}.${decimalPart}` : groupedInt;
};

/**
 * Format a raw numeric string for display: optionally prefixes "$" for currency
 * fields (after any minus sign) and groups large numbers with commas. An empty
 * value is returned unchanged so a placeholder can show.
 */
export const formatForDisplay = (
  raw: string,
  isCurrency: boolean,
  groupThousands: boolean,
): string => {
  if (raw === "") return "";
  const isNegative = raw.startsWith("-");
  const unsigned = isNegative ? raw.slice(1) : raw;
  const grouped = groupThousands ? groupThousandsDigits(unsigned) : unsigned;
  const withPrefix = isCurrency ? `$${grouped}` : grouped;
  return isNegative ? `-${withPrefix}` : withPrefix;
};

/**
 * Format a rate for a read-only cell so it reads the same as the editable rate
 * cells beside it, which render through NumericInput's idle currency display:
 * unit-correct precision, thousands separators, and a "$" prefix. Rate columns
 * are always currency, including PEPM.
 */
export function formatRateValueForDisplay(
  rate: number | null | undefined,
  unit?: string,
  coverageType?: string,
): string {
  return formatForDisplay(
    formatRateValue(rate, unit, coverageType),
    true,
    true,
  );
}

/**
 * Interface for age band with tobacco rates
 */
export interface AgeBandTobaccoPair {
  lower?: number;
  upper?: number;
  tobaccoRate?: Rate;
  nonTobaccoRate?: Rate;
  tobaccoEeRate?: Rate;
  tobaccoSpRate?: Rate;
  nonTobaccoEeRate?: Rate;
  nonTobaccoSpRate?: Rate;
}

/**
 * Group rates by age band with tobacco/non-tobacco split.
 * When EE/SP rates differ, populates the EE/SP-specific fields instead.
 */
export function groupRatesByAgeBandTobacco(
  rates: Rate[] | undefined,
): AgeBandTobaccoPair[] {
  if (!rates) return [];

  const groupMap = rates.reduce<Map<string, AgeBandTobaccoPair>>(
    (acc, rate) => {
      const key = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);

      if (!acc.has(key)) {
        acc.set(key, {
          lower: rate.ageBandLower,
          upper: rate.ageBandUpper,
        });
      }

      const group = acc.get(key)!;
      const isEmployee = rate.coverageTierCode === CoverageTierEnum.employee;
      const isSpouse = rate.coverageTierCode === CoverageTierEnum.spouseOnly;

      if (isEmployee || isSpouse) {
        if (rate.isTobaccoRated) {
          if (isEmployee) group.tobaccoEeRate = rate;
          else group.tobaccoSpRate = rate;
        } else {
          if (isEmployee) group.nonTobaccoEeRate = rate;
          else group.nonTobaccoSpRate = rate;
        }
      } else {
        if (rate.isTobaccoRated) {
          group.tobaccoRate = rate;
        } else {
          group.nonTobaccoRate = rate;
        }
      }

      return acc;
    },
    new Map(),
  );

  return sortByAgeBandBounds(Array.from(groupMap.values()));
}

/**
 * Interface for age band with gender rates
 */
export interface AgeBandGenderPair {
  lower?: number;
  upper?: number;
  maleRate?: Rate;
  femaleRate?: Rate;
  maleEeRate?: Rate;
  maleSpRate?: Rate;
  femaleEeRate?: Rate;
  femaleSpRate?: Rate;
}

/**
 * Group rates by age band with male/female split.
 * When EE/SP rates differ, populates the EE/SP-specific fields instead.
 */
export function groupRatesByAgeBandGender(
  rates: Rate[] | undefined,
): AgeBandGenderPair[] {
  if (!rates) return [];

  const groupMap = rates.reduce<Map<string, AgeBandGenderPair>>((acc, rate) => {
    const key = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);

    if (!acc.has(key)) {
      acc.set(key, {
        lower: rate.ageBandLower,
        upper: rate.ageBandUpper,
      });
    }

    const group = acc.get(key)!;
    const isEmployee = rate.coverageTierCode === CoverageTierEnum.employee;
    const isSpouse = rate.coverageTierCode === CoverageTierEnum.spouseOnly;

    if (isEmployee || isSpouse) {
      if (rate.gender === "male") {
        if (isEmployee) group.maleEeRate = rate;
        else group.maleSpRate = rate;
      } else if (rate.gender === "female") {
        if (isEmployee) group.femaleEeRate = rate;
        else group.femaleSpRate = rate;
      }
    } else {
      if (rate.gender === "male") {
        group.maleRate = rate;
      } else if (rate.gender === "female") {
        group.femaleRate = rate;
      }
    }

    return acc;
  }, new Map());

  return sortByAgeBandBounds(Array.from(groupMap.values()));
}

/**
 * Interface for age band with tiered rates
 */
export interface AgeBandTieredGroup {
  lower?: number;
  upper?: number;
  ratesByTier: Map<string, Rate>;
}

/**
 * Group rates by age band and coverage tier
 */
export function groupRatesByAgeBandAndTier(
  rates: Rate[] | undefined,
): AgeBandTieredGroup[] {
  if (!rates) return [];

  const groupMap = rates.reduce<Map<string, AgeBandTieredGroup>>(
    (acc, rate) => {
      const key = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);

      if (!acc.has(key)) {
        acc.set(key, {
          lower: rate.ageBandLower,
          upper: rate.ageBandUpper,
          ratesByTier: new Map(),
        });
      }

      const group = acc.get(key)!;
      if (rate.coverageTierCode) {
        group.ratesByTier.set(rate.coverageTierCode, rate);
      }

      return acc;
    },
    new Map(),
  );

  return sortByAgeBandBounds(Array.from(groupMap.values()));
}

/**
 * Extract unique coverage tiers from rates
 */
export function extractUniqueTiers(rates: Rate[] | undefined): string[] {
  if (!rates) return [];

  const uniqueTiers = rates.reduce<Set<string>>((acc, rate) => {
    if (rate.coverageTierCode) {
      acc.add(rate.coverageTierCode);
    }
    return acc;
  }, new Set());

  return Array.from(uniqueTiers);
}

/**
 * Extract unique age bands from rates
 */
export function extractUniqueAgeBands(
  rates: Rate[] | undefined,
): { lower?: number; upper?: number; key: string }[] {
  if (!rates) return [];

  const uniqueAgeBands = rates.reduce<
    Map<string, { lower?: number; upper?: number; key: string }>
  >((acc, rate) => {
    const key = createAgeBandKey(rate.ageBandLower, rate.ageBandUpper);
    if (!acc.has(key)) {
      acc.set(key, {
        lower: rate.ageBandLower,
        upper: rate.ageBandUpper,
        key,
      });
    }
    return acc;
  }, new Map());

  return sortByAgeBandBounds(Array.from(uniqueAgeBands.values()));
}
